import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrashIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
interface ConfirmDeleteDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
}

/** Destructive confirmation popup — focus starts on "Cancel" for safety. */
export function ConfirmDeleteDialog({
  open,
  title,
  message,
  onCancel,
  onConfirm
}: ConfirmDeleteDialogProps) {
  const {
    t
  } = useLanguage();
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => cancelRef.current?.focus(), 50);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onCancel]);
  return <AnimatePresence data-id="element-4027" data-mp="17vhs9l2k9vgv">
      {open && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15
    }} data-id="element-4028" data-mp="17uojkek1kvw3">
          <div className="absolute inset-0 bg-slate-900/40" onClick={onCancel} aria-hidden="true" data-id="element-4029" data-mp="13twb3nizuv0v" />
          <motion.div role="alertdialog" aria-modal="true" aria-labelledby="confirm-delete-title" aria-describedby="confirm-delete-message" className="relative w-full max-w-sm bg-white rounded-xl border border-slate-200 shadow-xl p-6" initial={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} data-id="element-4030" data-mp="1rks952e1mmgk">
            <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-red-50 text-red-600 mb-4" data-id="element-4031" data-mp="2p5jootiv7m8m">
              <TrashIcon className="w-5 h-5" data-id="element-4032" data-mp="2k5xaohv7q1yr" />
            </span>
            <h2 id="confirm-delete-title" className="text-base font-semibold text-[#0F172A]" data-id="element-4033" data-mp="2k4mb6u8svgdg">
              {title}
            </h2>
            <div id="confirm-delete-message" className="text-sm text-slate-500 mt-1.5 leading-relaxed" data-id="element-4034" data-mp="2a81483d7zv6z">
              {message}
            </div>
            <div className="flex justify-end gap-2 mt-6" data-id="element-4035" data-mp="21f7cv7ki15cr">
              <button ref={cancelRef} type="button" onClick={onCancel} className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-4036" data-mp="1wgmhpmk3tbv6">
                {t('Annuler', 'Cancel')}
              </button>
              <button type="button" onClick={onConfirm} className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors" data-id="element-4037" data-mp="1ihwmkcguhm9g">
                {t('Supprimer', 'Delete')}
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}